import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";

export default function AdminSettings() {
  const { settings, fetchSettings } = useSettings();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (settings && Object.keys(settings).length) setForm(settings); }, [settings]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/admin/settings", form);
      await fetchSettings();
      toast.success("Settings saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-gold" /></div>;

  const F = ({ label, k, ...props }) => (
    <div><Label>{label}</Label><Input value={form[k] || ""} onChange={set(k)} className="mt-1.5 rounded-sm" data-testid={`setting-${k}`} {...props} /></div>
  );

  return (
    <div className="max-w-3xl" data-testid="admin-settings-page">
      <h1 className="font-serif text-3xl lg:text-4xl text-obsidian mb-1">Business Settings</h1>
      <p className="text-sm text-muted-foreground mb-6">Configure your store's public information</p>

      <form onSubmit={save} className="space-y-6">
        <section className="bg-white border border-black/10 p-6 space-y-4">
          <Label className="overline text-gold-dark">Business Info</Label>
          <F label="Business Name" k="business_name" />
          <F label="Tagline" k="tagline" />
          <div><Label>About</Label><Textarea rows={4} value={form.about || ""} onChange={set("about")} className="mt-1.5 rounded-sm" data-testid="setting-about" /></div>
        </section>

        <section className="bg-white border-2 border-gold/40 p-6" data-testid="metal-rates-section">
          <Label className="overline text-gold-dark">Metal Rates, Making Charges & GST</Label>
          <p className="text-xs text-muted-foreground mt-1 mb-4">Set live rates per gram. All Gold (9K) & Silver (925) chain prices recalculate automatically. GP (plated) chains keep their manual price.</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>24K Gold Rate (₹ per gram)</Label>
              <Input type="number" step="0.01" value={form.gold_rate_24k ?? ""} onChange={set("gold_rate_24k")} className="mt-1.5 rounded-sm" data-testid="setting-gold_rate_24k" />
              <p className="text-[11px] text-muted-foreground mt-1">9K = 24K × 9/24 = ₹{form.gold_rate_24k ? (form.gold_rate_24k * 9 / 24).toFixed(2) : "0"}/g</p>
            </div>
            <div>
              <Label>995 Silver Rate (₹ per gram)</Label>
              <Input type="number" step="0.01" value={form.silver_rate_995 ?? ""} onChange={set("silver_rate_995")} className="mt-1.5 rounded-sm" data-testid="setting-silver_rate_995" />
              <p className="text-[11px] text-muted-foreground mt-1">925 = 995 × 925/995 = ₹{form.silver_rate_995 ? (form.silver_rate_995 * 925 / 995).toFixed(2) : "0"}/g</p>
            </div>
            <div>
              <Label>Gold Making Charge (%)</Label>
              <Input type="number" step="0.1" value={form.gold_making_pct ?? ""} onChange={set("gold_making_pct")} className="mt-1.5 rounded-sm" data-testid="setting-gold_making_pct" />
            </div>
            <div>
              <Label>Silver Making Charge (%)</Label>
              <Input type="number" step="0.1" value={form.silver_making_pct ?? ""} onChange={set("silver_making_pct")} className="mt-1.5 rounded-sm" data-testid="setting-silver_making_pct" />
            </div>
            <div>
              <Label>GST (%)</Label>
              <Input type="number" step="0.1" value={form.gst_pct ?? ""} onChange={set("gst_pct")} className="mt-1.5 rounded-sm" data-testid="setting-gst_pct" />
            </div>
          </div>
        </section>

        <section className="bg-white border border-black/10 p-6 grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2"><Label className="overline text-gold-dark">Contact</Label></div>
          <F label="Contact Email" k="contact_email" />
          <F label="Contact Phone" k="contact_phone" />
          <div className="sm:col-span-2"><F label="Address" k="address" /></div>
        </section>

        <section className="bg-white border border-black/10 p-6 grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2"><Label className="overline text-gold-dark">Social Links</Label></div>
          <F label="Facebook" k="facebook" />
          <F label="Instagram" k="instagram" />
          <F label="WhatsApp" k="whatsapp" />
          <F label="Twitter / X" k="twitter" />
        </section>

        <section className="bg-white border border-black/10 p-6 space-y-4">
          <Label className="overline text-gold-dark">Policies</Label>
          {[["Shipping & Delivery", "shipping"], ["Returns & Refunds", "returns"], ["Terms & Conditions", "terms"], ["Privacy Policy", "privacy"]].map(([label, k]) => (
            <div key={k}><Label>{label}</Label><Textarea rows={3} value={form[k] || ""} onChange={set(k)} className="mt-1.5 rounded-sm" data-testid={`setting-${k}`} /></div>
          ))}
        </section>

        <button type="submit" disabled={saving} className="inline-flex items-center gap-2 bg-gold text-obsidian font-semibold px-8 py-3 hover:bg-gold-light transition-colors disabled:opacity-50 rounded-sm" data-testid="save-settings-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Settings
        </button>
      </form>
    </div>
  );
}
